import Hue from '@assets/hue_yellow.svg';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import HorizontalAlignmentEnum from '@models/enums/HorizontalAlignmentEnum';
import BackgroundColourEnum from '@models/enums/IBackgroundColourEnum';
import VerticalAlignmentEnum from '@models/enums/VerticalAlignmentEnum';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';

interface Props {
  readonly title: string;
  readonly titleTextColour: 'black' | 'white';
  readonly linkText?: string;
  readonly link?: ILink;
  readonly image: IImage;
  readonly mobileImage?: IImage;
  readonly ctaCallback?: () => void;
  readonly text?: string;
  readonly desktopAlignment?: HorizontalAlignmentEnum;
  readonly mobileAlignment?: VerticalAlignmentEnum;
  readonly backgroundColour?:
    | BackgroundColourEnum.Yellow
    | BackgroundColourEnum.Black
    | BackgroundColourEnum.OffWhite
    | BackgroundColourEnum.ShadowGrey;
  readonly fullWidth?: boolean;
  readonly tint?: boolean;
  readonly glow?: boolean;
  readonly meta?: IMeta;
  readonly dataTestId?: string;
  readonly invertCTA?: boolean;
  readonly isInCarousel?: boolean;
  readonly anchorId?: string;
}

const mobileBackgroundColourClassName = {
  [BackgroundColourEnum.Yellow]: 'max-lg:bg-yellow',
  [BackgroundColourEnum.Black]: 'max-lg:bg-black-100',
  [BackgroundColourEnum.OffWhite]: 'max-lg:bg-grey-100',
  [BackgroundColourEnum.ShadowGrey]: 'max-lg:bg-grey-600',
};
const desktopBackgroundColourClassName = {
  [BackgroundColourEnum.Yellow]: 'lg:bg-yellow',
  [BackgroundColourEnum.Black]: 'lg:bg-black-100',
  [BackgroundColourEnum.OffWhite]: 'lg:bg-grey-100',
  [BackgroundColourEnum.ShadowGrey]: 'lg:bg-grey-600',
};
const desktopToMobileAlignmentMap = {
  [HorizontalAlignmentEnum.Left]: VerticalAlignmentEnum.Top,
  [HorizontalAlignmentEnum.Right]: VerticalAlignmentEnum.Bottom,
  [HorizontalAlignmentEnum.Center]: VerticalAlignmentEnum.Center,
};

const Banner = ({
  title,
  titleTextColour,
  desktopAlignment = HorizontalAlignmentEnum.Center,
  mobileAlignment: savedMobileAlignment,
  backgroundColour = BackgroundColourEnum.ShadowGrey,
  text,
  image,
  mobileImage,
  link,
  linkText,
  ctaCallback,
  fullWidth,
  tint,
  glow,
  meta,
  invertCTA = false,
  isInCarousel = false,
  dataTestId = 'Banner',
  anchorId = '',
}: Props) => {
  const mobileAlignment = (() => {
    if (savedMobileAlignment) return savedMobileAlignment;
    if (isInCarousel) return VerticalAlignmentEnum.Center;
    return desktopToMobileAlignmentMap[desktopAlignment];
  })();

  const isMobileTop = mobileAlignment === VerticalAlignmentEnum.Top;
  const isMobileBottom = mobileAlignment === VerticalAlignmentEnum.Bottom;
  const isMobileCenter = mobileAlignment === VerticalAlignmentEnum.Center;
  const isMobileFullWidth = isMobileCenter || fullWidth;
  const mobileBackgroundColour = isMobileCenter
    ? mobileBackgroundColourClassName[BackgroundColourEnum.Yellow]
    : mobileBackgroundColourClassName[backgroundColour];

  const isDesktopLeft = desktopAlignment === HorizontalAlignmentEnum.Left;
  const isDesktopRight = desktopAlignment === HorizontalAlignmentEnum.Right;
  const isDesktopCenter = desktopAlignment === HorizontalAlignmentEnum.Center;
  const isDesktopFullWidth = isDesktopCenter || fullWidth;
  const desktopBackgroundColour = isDesktopCenter
    ? desktopBackgroundColourClassName[BackgroundColourEnum.Yellow]
    : desktopBackgroundColourClassName[backgroundColour];

  return (
    <section
      className={classNames(
        'bg-white dark:bg-black-100',
        { 'component-padding': !isInCarousel, 'lg:h-full': isInCarousel },
        titleTextColour === 'white' ? 'text-white' : 'text-black-100'
      )}
      data-testid={dataTestId}
      {...(anchorId ? { id: `a-${anchorId}` } : {})}
    >
      <SoftLoadWrapper isAnimationDisabled={isInCarousel}>
        <div
          className={classNames('container relative', {
            'lg:h-full': isInCarousel,
          })}
        >
          <div
            className={classNames(
              'relative flex w-full max-xl:px-4 lg:min-h-[500px]',
              mobileBackgroundColour,
              desktopBackgroundColour,
              {
                'max-lg:items-center max-lg:justify-center': isMobileCenter,
                'lg:items-center lg:justify-center': isDesktopCenter,
                'max-lg:min-h-[335px]': isMobileCenter && !isInCarousel,
                'max-lg:min-h-[547px] lg:h-full': isInCarousel,
                'max-lg:grid max-lg:min-h-[577px] max-lg:grid-flow-row':
                  !isMobileCenter,
                'lg:grid lg:grid-cols-12': !isDesktopCenter,
              }
            )}
          >
            <div
              className={classNames({
                'max-lg:absolute max-lg:inset-0': isMobileFullWidth,
                'lg:absolute lg:inset-0': isDesktopFullWidth,
                'max-lg:relative max-lg:z-[1] max-lg:max-h-[400px]':
                  !isMobileFullWidth,
                'lg:relative lg:z-[1]': !isDesktopFullWidth,
                'lg:col-span-5 lg:col-start-8 xl:col-span-5 xl:col-start-7':
                  isDesktopLeft && !isDesktopFullWidth,
                'lg:col-span-5 lg:col-start-1 xl:col-span-5 xl:col-start-2':
                  isDesktopRight && !isDesktopFullWidth,
              })}
              data-epi-property-name={meta?.editHints?.image}
              data-testid={`${dataTestId}-Images-Container`}
            >
              {tint && (
                <div
                  className={classNames(
                    'absolute top-0 left-0 z-[1] h-full w-full bg-black-100 object-contain opacity-50 lg:opacity-30',
                    {
                      'max-lg:hidden': !isMobileFullWidth,
                      'lg:hidden': !isDesktopFullWidth,
                    }
                  )}
                  data-testid={`${dataTestId}-Tint`}
                />
              )}

              <Image
                image={image}
                classes="w-full"
                imageClasses={classNames('w-full h-full left-0 top-0 -z-1', {
                  'max-lg:object-cover max-lg:absolute': isMobileFullWidth,
                  'max-lg:object-contain': !isMobileFullWidth,
                  'lg:object-cover lg:absolute': isDesktopFullWidth,
                  'lg:object-contain xl:m-auto xl:w-[80%]': !isDesktopFullWidth,
                  'max-lg:hidden': mobileImage,
                })}
                dataTestId={`${dataTestId}-Image`}
              />

              {mobileImage && (
                <Image
                  image={mobileImage}
                  classes="w-full lg:hidden"
                  imageClasses={classNames('w-full h-full left-0 top-0 -z-1', {
                    'object-cover absolute': isMobileFullWidth,
                    'object-contain': !isMobileFullWidth,
                  })}
                  dataTestId={`${dataTestId}-Mobile-Image`}
                />
              )}

              {glow && (
                <img
                  src={`${Hue}`}
                  alt="hue"
                  className={classNames(
                    '-z-10 absolute top-0 h-full w-full object-contain',
                    {
                      'max-lg:hidden': isMobileFullWidth,
                      'lg:hidden': isDesktopFullWidth,
                      'opacity-50':
                        backgroundColour !== BackgroundColourEnum.OffWhite,
                    }
                  )}
                  data-testid={`${dataTestId}-Glow`}
                />
              )}
            </div>

            <div
              className={classNames(
                'relative z-10 flex h-full flex-col items-center py-13',
                text ? 'lg:gap-7.5' : 'lg:gap-10',
                {
                  'max-lg:justify-center max-lg:px-4': isMobileCenter,
                  'lg:justify-center lg:max-xl:px-4': isDesktopCenter,
                  'xl:w-1/3': isDesktopCenter && !isInCarousel,
                  'xl:w-[46%]': isDesktopCenter && isInCarousel,
                  'lg:items-start lg:justify-center': !isDesktopCenter,
                  'max-lg:order-first': isMobileTop,
                  'lg:order-first lg:col-span-5 lg:col-start-2 xl:col-start-2':
                    isDesktopLeft,
                  'xl:col-span-4': isDesktopLeft && !isInCarousel,
                  'xl:col-span-5': isDesktopLeft && isInCarousel,
                  'lg:col-span-5 lg:col-start-7 xl:col-span-4 xl:col-start-8':
                    isDesktopRight,
                  'max-lg:justify-end': isMobileBottom && isMobileFullWidth,
                }
              )}
            >
              <h2
                data-epi-property-name={meta?.editHints?.title}
                dangerouslySetInnerHTML={{ __html: title }}
                className={classNames(
                  'self-center text-center',
                  text ? 'max-lg:mb-2.5' : 'max-lg:mb-7.5',
                  {
                    'lg:self-start lg:text-left': !isDesktopCenter,
                  }
                )}
                data-testid={`${dataTestId}-Title`}
              />

              {text && (
                <p
                  className={classNames('body-one text-center max-lg:mb-5', {
                    'lg:text-left': !isDesktopCenter,
                  })}
                  data-testid={`${dataTestId}-Text`}
                >
                  {text}
                </p>
              )}

              {ctaCallback && linkText && (
                <Button
                  onClick={ctaCallback}
                  dataTestId={`${dataTestId}-CTA-Button`}
                  inverted={invertCTA}
                >
                  {linkText}
                </Button>
              )}

              {link?.url && link?.name && (
                <Button
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'selectContent',
                      content_type: 'cta_feature_block',
                      link_text: link.name,
                    });
                  }}
                  link={link}
                  dataTestId={`${dataTestId}-CTA-Link`}
                  inverted={invertCTA}
                />
              )}
            </div>
          </div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default Banner;
